/**
 * 公司基本資料設定(REQ-20260817004)。
 *
 * 系統原本沒有登記「我方公司」的地方 —— 抬頭是 REQ-20260817003 為了印請款明細
 * 臨時寄生在電子發票設定裡的。這頁把它獨立出來,填一次,所有印得出我方抬頭的文件
 * (月結對帳單、應收請款明細、藝品印刷訂單、進貨單)共用同一份。
 *
 * 這份設定**沒有機敏欄位**,所以不像 SMTP / 電子發票那樣有「留空保留原值」的欄位:
 * 表單上看到什麼,存進去就是什麼。
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CompanyProfileSettingInputSchema, type CompanyProfileSettingInput } from '@app/shared';
import { AddressInput } from '../../components/AddressInput';
import { useCompanyProfileSetting, useUpdateCompanyProfile } from './companyProfileApi';
import { getApiErrorMessage } from '../../lib/api-client';

export function CompanyProfileForm(): JSX.Element {
  const { data, isLoading, isError, error } = useCompanyProfileSetting();
  const updateMutation = useUpdateCompanyProfile();
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CompanyProfileSettingInput>({
    resolver: zodResolver(CompanyProfileSettingInputSchema),
    defaultValues: { name: '', taxId: '', address: '', phone: '', fax: '' },
  });

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        taxId: data.taxId,
        address: data.address,
        phone: data.phone,
        fax: data.fax,
      });
    }
  }, [data, reset]);

  if (isLoading) return <div role="status" className="text-slate-500">載入中...</div>;
  if (isError) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{getApiErrorMessage(error, '未知錯誤')}
      </div>
    );
  }

  const onSubmit = (values: CompanyProfileSettingInput): void => {
    setSavedFlash(null);
    updateMutation.mutate(values, {
      onSuccess: () => {
        setSavedFlash('已儲存');
        reset(values);
      },
    });
  };

  return (
    <section className="grid gap-4 max-w-xl">
      <h2 className="text-lg font-semibold">公司基本資料</h2>
      <p className="text-sm text-slate-600">
        這裡填一次,月結對帳單、應收請款明細、藝品印刷訂單、進貨單的「本公司」抬頭都會用同一份。
        沒填的欄位在文件上會印成「—」,不會擋住列印。
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3 bg-white border rounded p-4">
        <Row label="公司名稱" error={errors.name?.message}>
          <input {...register('name')} className={inputCls} placeholder="○○實業有限公司" />
        </Row>
        <Row label="統一編號" error={errors.taxId?.message}>
          <input
            {...register('taxId')}
            className={`${inputCls} w-40`}
            inputMode="numeric"
            placeholder="12345678"
          />
        </Row>
        <AddressRow label="公司地址" error={errors.address?.message}>
          <AddressInput
            label="公司地址"
            value={watch('address')}
            onChange={(next) => setValue('address', next, { shouldDirty: true })}
            inputClassName={inputCls}
            placeholder="台中市…"
          />
        </AddressRow>
        <Row label="電話" error={errors.phone?.message}>
          <input {...register('phone')} className={inputCls} placeholder="04-1234-5678" />
        </Row>
        <Row label="傳真" error={errors.fax?.message}>
          <input {...register('fax')} className={inputCls} placeholder="04-1234-5679" />
        </Row>

        {updateMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">
            儲存失敗：{getApiErrorMessage(updateMutation.error, '未知錯誤')}
          </div>
        ) : null}
        {savedFlash ? (
          <div role="status" className="text-green-700 text-sm">
            {savedFlash}
          </div>
        ) : null}

        <div>
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? '儲存中…' : '儲存'}
          </button>
        </div>
      </form>

      {/*
        電子發票設定裡也有一組賣方名稱 / 統編 —— 那是法規欄位(MIG XML 用),
        與這裡的顯示用抬頭是兩件事,刻意不連動。講清楚免得管理員兩邊對不上時來回改。
      */}
      <p className="text-xs text-slate-500">
        註:電子發票設定裡的「賣方營業人名稱 / 統一編號」是開立發票用的法規欄位,不受此頁影響。
      </p>
    </section>
  );
}

/**
 * 地址列(REQ-20260901004)。與 `Row` 同外觀,但用 `div` + `span` 而不是包住 children 的
 * `<label>` —— 郵遞區號 / 地址是兩個 input,包在同一個 label 裡會讓 getByLabelText
 * 抓到多個元素;標籤關聯改由各 input 自己的 aria-label 承接。
 */
function AddressRow({
  label,
  error,
  children,
}: {
  label: string;
  error: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="grid gap-1 text-sm">
      <span className="text-slate-700">{label}</span>
      {children}
      {error ? (
        <span role="alert" className="text-red-600 text-xs">
          {error}
        </span>
      ) : null}
    </div>
  );
}

function Row({
  label,
  error,
  children,
}: {
  label: string;
  error: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-slate-700">{label}</span>
      {children}
      {error ? (
        <span role="alert" className="text-red-600 text-xs">
          {error}
        </span>
      ) : null}
    </label>
  );
}

const inputCls = 'border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';
